// Cleaning a pasted listing URL.
//
// A listing address copied out of a search result, a collection page, an ad or a
// newsletter carries a tail that records HOW THE USER GOT THERE — "?_pos=2&
// _sid=32ba0d156&_ss=r" from a Shopify search, "?utm_source=..." from a campaign,
// "?fbclid=..." from an ad click. None of it says WHICH PRODUCT the page shows, so
// it is noise to the model, and the session ids are state that has no business
// leaving the machine.
//
// This is a deny-list on purpose. Dropping the whole query string would be simpler
// and wrong: "?variant=39847" on Shopify, "?sku=", "?color=" and Amazon's "?th=1"
// / "?psc=1" all SELECT which product (or which variant of it) the page shows.
// Strip those and the model reads a different product than the user meant — a
// silent, plausible wrong answer, which is the worst kind. When in doubt a
// parameter is kept: a stray tracking param costs nothing but noise.

// Matched case-insensitively against the parameter name.
const TRACKING_PARAMS = new Set([
  // Shopify's search / collection context — the case in the bug report. `_psid` is
  // the same family: it rides along on every link its search suggestions hand out.
  '_pos', '_sid', '_ss', '_psq', '_psid', '_v', '_fd',
  // Ad-click ids.
  'gclid', 'gclsrc', 'dclid', 'fbclid', 'msclkid', 'ttclid', 'twclid', 'igshid', 'yclid',
  'wbraid', 'gbraid',
  // Mailing-list ids.
  'mc_cid', 'mc_eid', 'ck_subscriber_id',
  // Amazon's referrer / layout crumbs. Note "th" and "psc" are deliberately NOT
  // here: they look like crumbs but pick the variant.
  'ref', 'ref_', '_encoding', 'pd_rd_r', 'pd_rd_w', 'pd_rd_wg',
  'pf_rd_p', 'pf_rd_r', 'pf_rd_s', 'pf_rd_t', 'pf_rd_i', 'pf_rd_m',
]);

// Whole families, matched by prefix: utm_source / utm_medium / utm_campaign / ...
const TRACKING_PREFIXES = ['utm_'];

const isTracking = (key: string): boolean => {
  const k = key.toLowerCase();
  return TRACKING_PARAMS.has(k) || TRACKING_PREFIXES.some((p) => k.startsWith(p));
};

// Strip the tracking tail off a pasted listing URL. Anything unparseable is handed
// back trimmed but untouched — the backend is what rejects a non-URL, and quietly
// rewriting something we didn't understand is worse than passing it through.
export function cleanListingUrl(raw: string): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return trimmed;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return trimmed;
  // Snapshot the keys first: deleting while iterating the live list skips entries.
  for (const key of [...url.searchParams.keys()]) {
    if (isTracking(key)) url.searchParams.delete(key);
  }
  return url.toString();
}

// A string as something safe to put in an href, or null if it isn't a link — a
// half-typed field, or text that is not a URL at all, which is why the buttons that
// open these come and go.
//
// Only http(s) qualifies. An href is whatever it is given, and `javascript:` in an
// anchor is not a link but a script the page gets to run — the one URL you must never
// hand to the browser is the one that is not an address.
export function linkHref(value: string): string | null {
  let parsed;
  try {
    parsed = new URL((value ?? '').trim());
  } catch {
    return null;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
}

// What a URL is worth READING: everything after the host.
//
// Four links to one product differ in their last few words — "…-en-tissu-beige-et-bois",
// "…-en-cuir-synthetique-noir" — and agree on the eighty characters before them. A row of
// fields showing "https://www.maisonpavane.com/products/lot-de-2-chaises-jos…" four times
// is four identical rows. The host is dropped into a leading ellipsis, so what is left is
// the part that tells them apart; if THAT still overflows, the box that shows it adds its
// own ellipsis at the other end.
//
// Anything that does not parse as a URL is shown whole. It is what somebody typed, and
// eliding the front of a half-typed address hides the half they are working on.
export function elideHost(value: string): string {
  const url = value.trim();
  try {
    const u = new URL(url);
    const rest = `${u.pathname}${u.search}${u.hash}`;
    return rest && rest !== '/' ? `…${rest}` : url;
  } catch {
    return url;
  }
}
